// Thin wrapper around the Rust secret_store commands.
//
// Behaviour matrix:
//   - Tauri desktop / iOS build → calls invoke('secret_seal' / 'secret_unseal')
//   - Tauri Android build       → commands not registered, falls back to plaintext
//   - Web/dev server (no Tauri) → falls back to plaintext
//
// Fallback writes a one-time warning to the console so the lack of sealing is
// visible during development without flooding logs. Callers should treat the
// return value as opaque storage form: pass whatever seal returned back into
// unseal verbatim.

let warnedAboutFallback = false
let availabilityChecked = false
let isAvailable = false

async function checkAvailability() {
  if (availabilityChecked) return isAvailable
  availabilityChecked = true

  try {
    const { invoke } = await import('@tauri-apps/api/core')
    // Seal an empty string as a probe. If the command isn't registered or
    // the keyring isn't reachable, this throws and we fall back globally.
    await invoke('secret_seal', { plaintext: '' })
    isAvailable = true
  } catch (err) {
    isAvailable = false
    if (!warnedAboutFallback) {
      warnedAboutFallback = true
      console.warn(
        '[SecretStore] Sealing unavailable on this build/platform — falling back to plaintext storage.',
        err?.message || err,
      )
    }
  }

  return isAvailable
}

export function useSecretStore() {
  const seal = async (plaintext) => {
    if (plaintext == null) return plaintext
    const value = typeof plaintext === 'string' ? plaintext : JSON.stringify(plaintext)
    if (!(await checkAvailability())) return value

    try {
      const { invoke } = await import('@tauri-apps/api/core')
      return await invoke('secret_seal', { plaintext: value })
    } catch (err) {
      console.error('[SecretStore] seal failed, returning plaintext:', err)
      return value
    }
  }

  const unseal = async (sealed) => {
    if (sealed == null || sealed === '') return sealed
    if (typeof sealed !== 'string') return sealed
    if (!(await checkAvailability())) return sealed

    try {
      const { invoke } = await import('@tauri-apps/api/core')
      return await invoke('secret_unseal', { ciphertext: sealed })
    } catch (err) {
      // Unseal failure usually means the value was stored before sealing was
      // enabled (legacy plaintext) — return as-is so callers can still use it.
      // True ciphertext that fails (tamper / wrong key) also lands here, so we
      // log noisily and surface the original to avoid silent breakage.
      console.warn('[SecretStore] unseal failed, treating as plaintext legacy value:', err?.message || err)
      return sealed
    }
  }

  return {
    seal,
    unseal,
    isAvailable: () => checkAvailability(),
  }
}
